import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import AdminLayout from "@/components/feature/AdminLayout";
import { getAllProfiles, updateProfile, getAllOrders } from "@/services/supabaseService";
import type { Profile } from "@/lib/supabase";

interface MemberWithRealOrders extends Profile {
  real_orders: number;
}

export default function TotalOrdersManage() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState<MemberWithRealOrders[]>([]);
  const [filtered, setFiltered] = useState<MemberWithRealOrders[]>([]);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [savingId, setSavingId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  const totalDiff = useMemo(() => {
    return filtered.reduce((sum, m) => sum + (m.total_orders ?? 0) - m.real_orders, 0);
  }, [filtered]);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [profiles, orders] = await Promise.all([
        getAllProfiles(),
        getAllOrders(),
      ]);

      // 統計每個用戶的真實訂單數
      const orderCountMap = new Map<string, number>();
      for (const order of orders) {
        const uid = order.user_id;
        if (!uid) continue;
        orderCountMap.set(uid, (orderCountMap.get(uid) || 0) + 1);
      }

      const merged: MemberWithRealOrders[] = profiles.map((p) => ({
        ...p,
        real_orders: orderCountMap.get(p.id) || 0,
      }));

      const sorted = merged.sort(
        (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
      );
      setMembers(sorted);
      setFiltered(sorted);
    } catch (err) {
      console.error("[TotalOrdersManage] 載入失敗:", err);
      showToast("載入資料失敗", "error");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const s = search.trim().toLowerCase();
    if (!s) {
      setFiltered(members);
      return;
    }
    setFiltered(
      members.filter(
        (m) =>
          m.name?.toLowerCase().includes(s) ||
          m.email?.toLowerCase().includes(s) ||
          m.phone?.toLowerCase().includes(s)
      )
    );
  }, [search, members]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-red-50 rounded-full">
            <i className="ri-lock-line text-2xl text-red-400"></i>
          </div>
          <h2 className="text-lg font-semibold text-gray-800 mb-2">無權限訪問</h2>
          <p className="text-sm text-gray-500 mb-4">僅管理員可查看此頁面</p>
          <button
            onClick={() => navigate("/")}
            className="px-5 py-2 bg-amber-500 text-white rounded-full text-sm font-medium hover:bg-amber-600 cursor-pointer"
          >
            返回首頁
          </button>
        </div>
      </div>
    );
  }

  function showToast(message: string, type: "success" | "error") {
    setToast({ message, type });
  }

  function startEdit(member: MemberWithRealOrders) {
    setEditingId(member.id);
    setEditValue(String(member.total_orders ?? 0));
  }

  function cancelEdit() {
    setEditingId(null);
    setEditValue("");
  }

  async function saveEdit(memberId: string) {
    const val = parseInt(editValue.trim(), 10);
    if (Number.isNaN(val) || val < 0) {
      showToast("請輸入有效的非負整數", "error");
      return;
    }

    setSavingId(memberId);
    try {
      await updateProfile(memberId, { total_orders: val });
      setMembers((prev) =>
        prev.map((m) => (m.id === memberId ? { ...m, total_orders: val } : m))
      );
      setFiltered((prev) =>
        prev.map((m) => (m.id === memberId ? { ...m, total_orders: val } : m))
      );
      setEditingId(null);
      setEditValue("");
      showToast("累計訂單數已更新", "success");
    } catch (err) {
      console.error("[TotalOrdersManage] 更新失敗:", err);
      showToast("更新失敗，請稍後再試", "error");
    } finally {
      setSavingId(null);
    }
  }

  const levelColor: Record<string, string> = {
    核心分銷: "bg-emerald-50 text-emerald-700",
    特約經銷: "bg-blue-50 text-blue-700",
    普通分銷: "bg-gray-50 text-gray-600",
  };

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl font-bold text-gray-900">累計訂單管理</h1>
            <p className="text-sm text-gray-500 mt-1">
              查看會員真實訂單數量，並可手動調整前台顯示的累計訂單數字
            </p>
          </div>
          <div className="text-sm text-gray-500">
            共 {filtered.length} 位分銷商
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-xs text-gray-400 mb-1">總真實訂單數</p>
            <p className="text-xl font-bold text-gray-900">
              {members.reduce((sum, m) => sum + m.real_orders, 0).toLocaleString()} 件
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-xs text-gray-400 mb-1">總顯示訂單數</p>
            <p className="text-xl font-bold text-amber-600">
              {members.reduce((sum, m) => sum + (m.total_orders ?? 0), 0).toLocaleString()} 件
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-xs text-gray-400 mb-1">差額（顯示 - 真實）</p>
            <p className={`text-xl font-bold ${totalDiff >= 0 ? "text-emerald-600" : "text-red-500"}`}>
              {totalDiff >= 0 ? "+" : ""}{totalDiff.toLocaleString()} 件
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-xl border border-gray-100 p-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
              <i className="ri-search-line text-amber-600 text-sm"></i>
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="搜尋名稱 / Email / 電話..."
              className="flex-1 text-sm outline-none placeholder-gray-400"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center text-gray-400 text-sm">載入中...</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-sm">
              {search ? "沒有符合搜尋條件的分銷商" : "暫無分銷商資料"}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left px-5 py-3 font-medium text-gray-500 w-14">#</th>
                    <th className="text-left px-5 py-3 font-medium text-gray-500">分銷商</th>
                    <th className="text-left px-5 py-3 font-medium text-gray-500">等級</th>
                    <th className="text-left px-5 py-3 font-medium text-gray-500">電話</th>
                    <th className="text-center px-5 py-3 font-medium text-gray-500">真實訂單數</th>
                    <th className="text-center px-5 py-3 font-medium text-gray-500">顯示訂單數</th>
                    <th className="text-center px-5 py-3 font-medium text-gray-500 w-24">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.map((member, idx) => {
                    const diff = (member.total_orders ?? 0) - member.real_orders;
                    return (
                      <tr key={member.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-5 py-3.5 text-gray-400">{idx + 1}</td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 text-xs font-bold flex-shrink-0">
                              {member.name?.charAt(0) || "?"}
                            </div>
                            <div>
                              <p className="font-medium text-gray-800">{member.name || "未命名"}</p>
                              <p className="text-xs text-gray-400">{member.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              levelColor[member.level] || "bg-gray-50 text-gray-600"
                            }`}
                          >
                            {member.level}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-gray-600">
                          {member.phone || "—"}
                        </td>
                        <td className="px-5 py-3.5 text-center">
                          <span className="text-base font-semibold text-gray-900">
                            {member.real_orders}
                          </span>
                          <span className="text-xs text-gray-400 ml-1">件</span>
                        </td>
                        <td className="px-5 py-3.5 text-center">
                          {editingId === member.id ? (
                            <div className="flex items-center justify-center gap-2">
                              <input
                                type="number"
                                min={0}
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") saveEdit(member.id);
                                  if (e.key === "Escape") cancelEdit();
                                }}
                                className="w-20 text-center text-sm border border-gray-200 rounded-md px-2 py-1 outline-none focus:border-amber-400"
                                autoFocus
                              />
                              <button
                                onClick={() => saveEdit(member.id)}
                                disabled={savingId === member.id}
                                className="w-7 h-7 flex items-center justify-center rounded-md bg-emerald-50 text-emerald-600 hover:bg-emerald-100 cursor-pointer disabled:opacity-50"
                              >
                                {savingId === member.id ? (
                                  <i className="ri-loader-4-line animate-spin text-xs"></i>
                                ) : (
                                  <i className="ri-check-line text-xs"></i>
                                )}
                              </button>
                              <button
                                onClick={cancelEdit}
                                disabled={savingId === member.id}
                                className="w-7 h-7 flex items-center justify-center rounded-md bg-gray-50 text-gray-500 hover:bg-gray-100 cursor-pointer disabled:opacity-50"
                              >
                                <i className="ri-close-line text-xs"></i>
                              </button>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center">
                              <span className="text-base font-semibold text-gray-900">
                                {member.total_orders ?? 0}
                              </span>
                              {diff !== 0 && (
                                <span className={`text-[10px] ${diff > 0 ? "text-emerald-500" : "text-red-400"}`}>
                                  {diff > 0 ? "+" : ""}{diff} 件
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-5 py-3.5 text-center">
                          {editingId !== member.id && (
                            <button
                              onClick={() => startEdit(member)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-amber-600 bg-amber-50 hover:bg-amber-100 rounded-md transition-colors cursor-pointer"
                            >
                              <i className="ri-edit-line"></i>
                              修改
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Toast */}
        {toast && (
          <div className="fixed bottom-6 right-6 z-50">
            <div
              className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
                toast.type === "success"
                  ? "bg-emerald-500 text-white"
                  : "bg-red-500 text-white"
              }`}
            >
              <i
                className={
                  toast.type === "success"
                    ? "ri-checkbox-circle-line"
                    : "ri-error-warning-line"
                }
              ></i>
              {toast.message}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}